import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Loader2, AlertCircle, Download, ExternalLink } from 'lucide-react';
import { userService } from '../../../_services';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import moment from 'moment';

/**
 * Tier Report Dialog
 * Modernized modal for downloading tier reports
 * Supports: User Tier Report, Minimum Payout Report
 */
function TierReportDialog({ open, onOpenChange, title }) {
    const [selectedMonth, setSelectedMonth] = useState('');
    const [monthData, setMonthData] = useState([]);
    const [loadingMonths, setLoadingMonths] = useState(false);
    const [error, setError] = useState('');
    const [downloading, setDownloading] = useState(false);

    // Determine report type
    const isUserTier = title === 'User Tier';
    const isMinimumPayout = title === 'Minimum Payout';

    // Fetch month list when dialog opens
    useEffect(() => {
        if (!open) {
            setError('');
            setDownloading(false);
            return;
        }

        const fetchMonths = async () => {
            setLoadingMonths(true);
            try {
                const Usermeta_Data = window.atob(localStorage.getItem('ajs_user_id'));
                const userId = Usermeta_Data.split(':')[0];
                const userrole = window.atob(localStorage.getItem('_Host_Pro_type'));

                let apidata;
                if (isMinimumPayout) {
                    apidata = await userService.GetListOfMonthPayout(userId, userrole);
                } else {
                    apidata = await userService.GetListOfMonth(userId, userrole);
                }

                if (apidata && apidata.length > 0) {
                    setMonthData(apidata);
                    // Set first month as default
                    const firstVal = isMinimumPayout ? apidata[0].monthname : apidata[0].monthvalue;
                    setSelectedMonth(firstVal);
                } else {
                    setMonthData([]);
                }
            } catch (err) {
                console.error('Error fetching months:', err);
                setMonthData([]);
            } finally {
                setLoadingMonths(false);
            }
        };

        fetchMonths();
    }, [open, isMinimumPayout]);

    // Download file helper using ExcelJS + FileSaver (works reliably inside Shadcn Dialog portals)
    const downloadFile = async (data, month) => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet('User Tier Report');
        if (data.length > 0) {
            sheet.columns = Object.keys(data[0]).map(key => ({ header: key, key }));
            sheet.addRows(data);
        }
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(
            new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' }),
            `User_Tier_Report_${moment(new Date()).format('yyyyMMDDhhmmss')}.xlsx`
        );
    };

    // Download report handler
    const handleDownload = async () => {
        if (!selectedMonth) {
            setError('Please select a month');
            return;
        }

        setDownloading(true);
        setError('');

        try {
            if (isUserTier) {
                // Call GetUserTierReport with just the Month value (matching dev-v3 and user.service.js signature)
                const response = await userService.GetUserTierReport(selectedMonth);

                if (!response || response.length === 0) {
                    setError('No data available for the selected month');
                    setDownloading(false);
                    return;
                }

                // Transform data for Excel matching dev-v3 column names
                const tableData = response.map(item => ({
                    'Group Number': item.groupNumber,
                    'Affiliate Name': item.affiliateName,
                    'Parent Affiliate Name': item.parent_Affiliate_Name,
                    'Number of Paid Transactions': item.number_of_Paid_Transactions,
                    'Rank/Tier': item.tierName
                }));

                downloadFile(tableData, selectedMonth.split('T')[0]);

            } else if (isMinimumPayout) {
                // Open external reporting URL for Minimum Payout
                const currentUrl = window.location.href;
                const isDevEnvironment = currentUrl.toLowerCase().includes("localhost:") ||
                    currentUrl.toLowerCase().includes("dev-backoffice") || currentUrl.toLowerCase().includes("shadcn-backoffice");

                if (isDevEnvironment) {
                    window.open('https://dev.glichealth.com/report/payout_report_jd_IsPayable?&name=patient%20rx%20discount&wh_id=61&csvfor=ds&m=' + selectedMonth + '&processor=0&ObjectId=B8B95710360E4BCB', '_blank');
                } else {
                    window.open('https://reporting.glichealth.com/report/payout_report_jd_IsPayable?&name=patient%20rx%20discount&wh_id=61&csvfor=ds&m=' + selectedMonth + '&processor=0&ObjectId=772BB34B1E0E4BA1', '_blank');
                }
            }

        } catch (err) {
            console.error('Error downloading report:', err);
            setError('Failed to download report. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold text-primary">
                        {title} Report
                    </DialogTitle>
                    <DialogDescription>
                        {isUserTier
                            ? 'Select a month to download the tier report as Excel'
                            : 'Select a month to view the minimum payout report'
                        }
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Month Selector */}
                    <div className="space-y-2">
                        <Label htmlFor="month-select">Choose Date</Label>
                        {loadingMonths ? (
                            <div className="flex items-center gap-2 text-primary/70">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Loading months...</span>
                            </div>
                        ) : monthData && monthData.length > 0 ? (
                            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                                <SelectTrigger id="month-select" className="w-full">
                                    <SelectValue placeholder="Select Month" />
                                </SelectTrigger>
                                <SelectContent>
                                    {isMinimumPayout ? (
                                        monthData.map((item, index) => (
                                            <SelectItem key={index} value={item.monthname}>
                                                {item.monthname}
                                            </SelectItem>
                                        ))
                                    ) : (
                                        monthData.map((item, index) => (
                                            <SelectItem key={index} value={item.monthvalue}>
                                                {item.monthname}
                                            </SelectItem>
                                        ))
                                    )}
                                </SelectContent>
                            </Select>
                        ) : (
                            <Alert variant="destructive">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                    No months available. Please try again later.
                                </AlertDescription>
                            </Alert>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <Alert variant="destructive">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={downloading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDownload}
                        disabled={downloading || loadingMonths || !selectedMonth}
                        variant="secondary"
                    >
                        {downloading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {isUserTier ? 'Downloading...' : 'Opening...'}
                            </>
                        ) : (
                            <>
                                {isUserTier ? (
                                    <>
                                        <Download className="mr-2 h-4 w-4" />
                                        Download
                                    </>
                                ) : (
                                    <>
                                        <ExternalLink className="mr-2 h-4 w-4" />
                                        View Report
                                    </>
                                )}
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default TierReportDialog;
